import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { _log } from '@shared/aux_helper_environment';
import { kayshFlushValue } from '@karacas/kaysh';
import { DataAgentSSEService } from 'app/services/agents/data-agent-sse.service';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { InsightDetailModalComponent } from './insight-detail-modal.component';
import { InsightPriority, InsightSuggestion, InsightType } from './insights-suggestions-mock.data';

@Component({
  selector: 'insights-suggestions-panel',
  templateUrl: './insights-suggestions-panel.component.html',
  styleUrls: ['./insights-suggestions-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InsightsSuggestionsPanelComponent implements OnInit {
  suggestions: InsightSuggestion[] = [];
  panelOpened = false;
  isLoading = false;
  currentSort: 'impact' | 'date' = 'impact';

  constructor(
    private translate: IanTranslateService,
    private dialog: MatDialog,
    private _fuseSidebarService: FuseSidebarService,
    private dataAgentService: DataAgentSSEService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this._fuseSidebarService.getSidebar('insightsSuggestionsPanel').openedChanged.subscribe(data => {
      this.panelOpened = data;
      if (this.panelOpened) {
        this.loadHardcodedSuggestions();
      }
    });
  }

  loadHardcodedSuggestions(): void {
    _log('[loadHardcodedSuggestions] Loading hardcoded suggestions...');
    this.isLoading = true;
    // Bust kaysh cache so the panel always shows fresh data when opened
    try {
      kayshFlushValue('getInsightsSuggestions', null);
    } catch (_) {}
    this.dataAgentService.getInsightsSuggestions().subscribe(suggestions => {
      this.suggestions = suggestions;
      this.sortSuggestions();
      this.isLoading = false;
      _log('[loadHardcodedSuggestions] Loaded', this.suggestions.length, 'suggestions');
    });
  }

  setSort(sort: 'impact' | 'date'): void {
    this.currentSort = sort;
    this.sortSuggestions();
  }

  sortSuggestions(): void {
    if (this.currentSort === 'impact') {
      this.suggestions.sort((a, b) => (b.estimatedImpact || 0) - (a.estimatedImpact || 0));
    } else {
      this.suggestions.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
    }
  }

  acceptSuggestion(suggestion: InsightSuggestion): void {
    suggestion.status = 'accepted';
    _log('Sugerencia aceptada:', suggestion);
    // Aquí iría la lógica para procesar la aceptación
  }

  rejectSuggestion(suggestion: InsightSuggestion): void {
    suggestion.status = 'rejected';
    _log('Sugerencia rechazada:', suggestion);
    // Aquí iría la lógica para procesar el rechazo
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      const key = diffMins === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.MINUTES' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.MINUTES_PLURAL';
      const translation = this.translate.instant(key);
      return translation ? translation.replace('{{count}}', diffMins.toString()) : `${diffMins} min`;
    } else if (diffHours < 24) {
      const key = diffHours === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.HOURS' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.HOURS_PLURAL';
      const translation = this.translate.instant(key);
      return translation ? translation.replace('{{count}}', diffHours.toString()) : `${diffHours} h`;
    } else if (diffDays < 7) {
      const key = diffDays === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.DAYS' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.DAYS_PLURAL';
      const translation = this.translate.instant(key);
      return translation ? translation.replace('{{count}}', diffDays.toString()) : `${diffDays} d`;
    } else {
      const lang = this.translate._translateService.currentLang || 'es';
      return date.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  getPendingCount(): number {
    return this.suggestions.filter(s => s.status === 'pending').length;
  }

  getTitle(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.TITLE') || 'Sugerencias de Insights';
  }

  getPendingBadgeText(): string {
    const count = this.getPendingCount();
    const key = count === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.PENDING_BADGE' : 'INSIGHTS_SUGGESTIONS_PANEL.PENDING_BADGE_PLURAL';
    return this.translate.instant(key) || (count === 1 ? 'pendiente' : 'pendientes');
  }

  getAcceptButtonText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.ACCEPT_BUTTON') || 'Aceptar';
  }

  getRejectButtonText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.REJECT_BUTTON') || 'Rechazar';
  }

  getStatusText(status: 'accepted' | 'rejected'): string {
    const key = status === 'accepted' ? 'INSIGHTS_SUGGESTIONS_PANEL.STATUS_ACCEPTED' : 'INSIGHTS_SUGGESTIONS_PANEL.STATUS_REJECTED';
    return this.translate.instant(key) || (status === 'accepted' ? 'Aceptada' : 'Rechazada');
  }

  getEmptyMessage(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.EMPTY_MESSAGE') || 'No hay sugerencias disponibles';
  }

  getViewDetailsText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.VIEW_DETAILS') || 'Ver detalles';
  }

  getTypeLabel(type: InsightType): string {
    const key = `INSIGHTS_SUGGESTIONS_PANEL.TYPE.${type.toUpperCase()}`;
    return this.translate.instant(key) || type;
  }

  getPriorityLabel(priority: InsightPriority): string {
    const key = `INSIGHTS_SUGGESTIONS_PANEL.PRIORITY.${priority.toUpperCase()}`;
    return this.translate.instant(key) || priority;
  }

  formatCurrency(amount: number): string {
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `$ ${formatted}`;
  }

  getEstimatedImpactLabel(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.ESTIMATED_IMPACT');
  }

  getSortByText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SORT_BY') || 'Ordenar por';
  }

  getSortImpactText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SORT_IMPACT') || 'Impacto estimado';
  }

  getSortDateText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.SORT_DATE') || 'Fecha';
  }

  viewDetails(suggestion: InsightSuggestion): void {
    _log('[viewDetails] Opening modal for suggestion:', suggestion.id, suggestion.title);

    // Usar tableData ya mapeado en el servicio
    const tableData = suggestion.tableData;
    _log('[viewDetails] Using pre-calculated tableData:', tableData ? 'YES' : 'NO');
    this.openModal(suggestion, tableData);
  }

  private openModal(suggestion: InsightSuggestion, tableData: any): void {
    const dialogRef = this.dialog.open(InsightDetailModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'insight-detail-dialog',
      data: {
        id: suggestion.id,
        title: suggestion.title,
        description: suggestion.description,
        date: suggestion.date,
        type: suggestion.type,
        priority: suggestion.priority,
        estimatedImpact: suggestion.estimatedImpact,
        detailedReport: suggestion.detailedReport || 'No hay informe detallado disponible.',
        tableData: tableData,
        insight_id: suggestion.insight_id,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'accept') {
        this.acceptSuggestion(suggestion);
      } else if (result?.action === 'reject') {
        this.rejectSuggestion(suggestion);
      } else if (result?.action === 'view_chat') {
        _log('[InsightsSuggestionsPanel] User requested to view chat for insight:', result.insightId);
        // Navegar a la ruta de sesión específica
        this.router.navigate(['/ai-chat/session', result.insightId]);
      }
    });
  }
}
